<script setup>
import { computed, defineProps } from "vue";

import AppCard from "@/components/AppCard";
import { capitalize, isBlank } from "@/utils/formatters";

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

const value = (key) => {
  const raw = props.user?.[key];
  return isBlank(raw) ? "" : String(raw).trim();
};

const name = computed(() => {
  const raw = value("name");
  return raw ? capitalize(raw) : "";
});

const initials = computed(() => {
  const words = name.value.split(" ").filter(Boolean);
  if (!words.length) {
    return "?";
  }

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
});

// A contact can carry several addresses in a single field
const emails = computed(() =>
  value("email")
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
);

const fields = computed(() => [
  {
    key: "name",
    icon: "uil uil-user",
    label: "profile.name",
    value: name.value,
  },
  {
    key: "vat",
    icon: "uil uil-postcard",
    label: "profile.vat",
    value: value("vat"),
  },
  {
    key: "razon_social",
    icon: "uil uil-building",
    label: "profile.business_name",
    value: value("razon_social"),
  },
  {
    key: "phone",
    icon: "uil uil-phone",
    label: "profile.phone",
    value: value("phone") || value("mobile"),
  },
]);
</script>

<template lang="pug">
.profile-card
  AppCard.profile-card__hero(padding="lg" elevated)
    .hero
      .hero__avatar {{ initials }}
      .hero__heading
        h1.hero__name {{ name || $t("common.not_specified") }}
        p.hero__email(v-if="emails.length") {{ emails[0] }}
      router-link.hero__action(:to="{ name: 'ChangePass' }")
        i.uil.uil-asterisk(aria-hidden="true")
        span {{ $t("userOptions.changePass") }}

  AppCard.profile-card__details(padding="lg")
    .details
      .details__item.details__item--wide
        .details__icon
          i.uil.uil-envelope(aria-hidden="true")
        .details__content
          h4 {{ $t("profile.email") }}
          ul.details__emails(v-if="emails.length")
            li(v-for="email in emails" :key="email")
              a(:href="`mailto:${email}`") {{ email }}
          span.details__empty(v-else) {{ $t("common.not_specified") }}
      .details__item(v-for="field in fields" :key="field.key")
        .details__icon
          i(:class="field.icon" aria-hidden="true")
        .details__content
          h4 {{ $t(field.label) }}
          span(:class="{ 'details__empty': !field.value }") {{ field.value || $t("common.not_specified") }}
</template>

<style lang="scss" scoped>
.profile-card {
  @apply pb-6;
  @apply max-w-3xl;

  &__hero {
    @apply mb-6;
  }
}

.hero {
  @apply flex;
  @apply flex-col;
  @apply items-center;
  @apply gap-4;
  @apply text-center;
  @screen sm {
    @apply flex-row;
    @apply items-center;
    @apply text-left;
  }

  &__avatar {
    @apply flex;
    @apply flex-shrink-0;
    @apply items-center;
    @apply justify-center;
    @apply w-16;
    @apply h-16;
    @apply rounded-full;
    @apply bg-gignetgreen-200;
    @apply text-gignetgreen-800;
    @apply text-xl;
    @apply font-semibold;
    @apply select-none;
  }

  &__heading {
    @apply flex-1;
    @apply min-w-0;
  }

  &__name {
    @apply text-xl;
    @apply font-semibold;
    @apply text-bluegray-700;
    @apply leading-snug;
  }

  &__email {
    @apply text-sm;
    @apply text-bluegray-400;
    @apply mt-1;
    overflow-wrap: anywhere;
  }

  &__action {
    @apply inline-flex;
    @apply flex-shrink-0;
    @apply items-center;
    @apply justify-center;
    @apply gap-2;
    @apply px-4;
    @apply py-2.5;
    @apply rounded-lg;
    @apply bg-bluegray-100;
    @apply text-bluegray-600;
    @apply text-sm;
    @apply font-medium;
    @apply whitespace-nowrap;
    @apply outline-none;
    @apply transition-colors;
    @apply duration-200;

    i {
      @apply text-base;
      @apply leading-none;
    }

    &:hover {
      @apply bg-bluegray-200;
      @apply text-bluegray-700;
    }

    &:focus-visible {
      @apply ring-2;
      @apply ring-gignetblue-300;
      @apply ring-offset-2;
    }
  }
}

.details {
  @apply grid;
  @apply grid-cols-1;
  @apply gap-5;
  @screen sm {
    @apply grid-cols-2;
  }

  &__item {
    @apply flex;
    @apply items-start;
    @apply gap-3;
    @apply min-w-0;

    &--wide {
      @screen sm {
        @apply col-span-2;
      }
    }
  }

  &__icon {
    @apply flex;
    @apply flex-shrink-0;
    @apply items-center;
    @apply justify-center;
    @apply w-9;
    @apply h-9;
    @apply rounded-lg;
    @apply bg-bluegray-50;

    i {
      @apply text-lg;
      @apply leading-none;
      @apply text-bluegray-400;
    }
  }

  &__content {
    @apply flex-1;
    @apply min-w-0;

    h4 {
      @apply text-xs;
      @apply font-medium;
      @apply text-bluegray-400;
      @apply uppercase;
      @apply tracking-wide;
    }

    span {
      @apply block;
      @apply text-sm;
      @apply text-bluegray-700;
      // Values are free text (business names, tax ids): wrap instead of
      // pushing the card wider
      overflow-wrap: anywhere;
    }
  }

  &__emails {
    @apply flex;
    @apply flex-col;
    @apply gap-1;
    @apply mt-0.5;

    a {
      @apply block;
      @apply text-sm;
      @apply text-gignetblue-500;
      @apply transition-colors;
      @apply duration-200;
      overflow-wrap: anywhere;

      &:hover {
        @apply text-gignetblue-600;
        @apply underline;
      }
    }
  }

  // Declared after `&__content span` so it wins at the same specificity
  &__content &__empty {
    @apply text-bluegray-300;
  }
}
</style>
